import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { useSiteContent } from "@/hooks/useContent";

export default function Hero() {
  const { content } = useSiteContent();
  const hero = content?.hero || {};
  const desktopImg = hero.image;
  const mobileImg = hero.image_mobile || hero.image;

  // Rotating taglines — first entry is the CMS-configured headline, then CMS-managed additional taglines
  const taglines = useMemo(() => {
    const primary = { line1: hero.heading_line1, line2: hero.heading_line2, line3: hero.heading_line3 };
    const primaryHasContent = !!(primary.line1 || primary.line2 || primary.line3);
    const extras = (hero.taglines || []).filter((t) => t && (t.line1 || t.line2 || t.line3));
    const list = [];
    if (primaryHasContent) list.push(primary);
    list.push(...extras);
    return list.length ? list : [{ line1: "", line2: "", line3: "" }];
  }, [hero.heading_line1, hero.heading_line2, hero.heading_line3, hero.taglines]);

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (taglines.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % taglines.length), 5000);
    return () => clearInterval(t);
  }, [taglines.length]);

  const current = taglines[idx] || {};

  // Idle-nudge: pulse the CALL ME button on mobile after 3s of no interaction
  const [idlePulse, setIdlePulse] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stop = () => setIdlePulse(false);
    const t = setTimeout(() => setIdlePulse(true), 3000);
    window.addEventListener("scroll", stop, { once: true, passive: true });
    window.addEventListener("touchstart", stop, { once: true, passive: true });
    window.addEventListener("click", stop, { once: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", stop);
      window.removeEventListener("touchstart", stop);
      window.removeEventListener("click", stop);
    };
  }, []);

  return (
    <section id="hero" data-testid="hero-section" className="relative w-full h-[100svh] md:h-screen -mt-12 md:-mt-14 min-h-[500px] overflow-hidden bg-gg-black">
      {/* Desktop hero image */}
      {desktopImg && (
        <motion.img
          key={`desk-${desktopImg}`}
          src={desktopImg}
          alt="GG Fitness"
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2 }}
          className="absolute inset-0 w-full h-full object-cover hidden md:block"
        />
      )}
      {/* Mobile hero image */}
      {mobileImg && (
        <motion.img
          key={`mob-${mobileImg}`}
          src={mobileImg}
          alt="GG Fitness"
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2 }}
          className="absolute inset-0 w-full h-full object-cover md:hidden"
        />
      )}

      {/* Bottom gradient — stronger on mobile for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

      {/* Text content pinned to bottom with safe spacing — wrapped in max-w-7xl so it aligns with navbar and page content across breakpoints. Extra bottom-padding on mobile to clear the browser URL bar safely. */}
      <div className="absolute bottom-10 sm:bottom-8 lg:bottom-10 left-0 right-0 z-10 px-4 sm:px-8 lg:px-6 xl:px-6 lg:pr-24 xl:pr-6">
        <div className="lg:max-w-7xl lg:mx-auto">
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.3 }}>
          <span className="font-heading text-gg-red text-[9px] sm:text-xs uppercase tracking-[0.3em] sm:tracking-[0.4em] font-semibold">{hero.badge}</span>
        </motion.div>

        {/* Rotating heading */}
        <div className="relative mt-1 min-h-[5.5rem] sm:min-h-[7rem] md:min-h-[8.5rem] lg:min-h-[9rem] xl:min-h-[11rem]">
          <AnimatePresence mode="wait">
            <motion.h1
              key={idx}
              initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -24, filter: "blur(6px)" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 font-heading text-[1.7rem] sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-extrabold uppercase tracking-tight leading-[0.9] optical-align"
              data-testid="hero-heading"
            >
              <span className="block">{current.line1}</span>
              <span className="text-gg-red">{current.line2} </span>
              <span>{current.line3}</span>
            </motion.h1>
          </AnimatePresence>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-3 sm:mt-4 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 pr-0 sm:pr-24 lg:pr-0 max-w-[260px] sm:max-w-none"
        >
          <Link
            to="/contact"
            data-testid="hero-cta-button"
            className="group/cta inline-flex items-center justify-center gap-2 font-heading text-[11px] uppercase tracking-widest bg-gg-red text-white px-5 py-2.5 rounded-lg hover:bg-gg-red-hover transition-all duration-300 hover:shadow-xl hover:shadow-gg-red/30 hover:scale-[1.03] btn-press"
          >
            {hero.cta_primary}
            <ArrowRight size={14} className="group-hover/cta:translate-x-1.5 transition-transform duration-300" />
          </Link>
          <Link
            to="/personal-training"
            data-testid="hero-services-link"
            className="group/svc inline-flex items-center justify-center gap-2 font-heading text-[11px] uppercase tracking-widest border border-white/20 text-white px-5 py-2.5 rounded-lg hover:border-gg-red hover:text-gg-red hover:bg-gg-red/5 transition-all duration-300 btn-press"
          >
            {hero.cta_secondary}
            <ArrowRight size={14} className="group-hover/svc:translate-x-1.5 transition-transform duration-300" />
          </Link>
          <a
            href="tel:+919153020904"
            data-testid="hero-call-link"
            onClick={() => setIdlePulse(false)}
            className={`inline-flex items-center justify-center gap-2 font-heading text-[11px] uppercase tracking-widest border border-white/20 text-white px-5 py-2.5 rounded-lg hover:border-white/40 hover:bg-white/5 transition-all duration-300 btn-press ${idlePulse ? "call-idle-pulse sm:animate-none" : ""}`}
          >
            <Phone size={14} />
            Call Me
          </a>
        </motion.div>
        </div>
      </div>
    </section>
  );
}
